import * as React from "react";
import { Loader2, Send, CheckCircle2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useSendAdminEmail } from "@/hooks/useAdmin";
import { ApiError } from "@/lib/api";

/**
 * Ad-hoc admin email — send a one-off branded message to any address from the
 * HosterDash mailbox. Useful for support follow-ups, manual notices, etc.
 */
export function AdminEmailPage() {
  const send = useSendAdminEmail();
  const { toast } = useToast();
  const [to, setTo] = React.useState("");
  const [subject, setSubject] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [sentTo, setSentTo] = React.useState<string | null>(null);

  const valid = /\S+@\S+\.\S+/.test(to) && subject.trim().length > 0 && message.trim().length > 0;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid || send.isPending) return;
    try {
      const r = await send.mutateAsync({ to_email: to.trim(), subject: subject.trim(), message });
      setSentTo(r.sent_to);
      toast({ title: "Email sent", description: `Delivered to ${r.sent_to}`, variant: "success" });
      setSubject("");
      setMessage("");
    } catch (err) {
      toast({
        title: "Couldn't send",
        description: err instanceof ApiError ? err.message : "Check SMTP settings and try again.",
        variant: "destructive"
      });
    }
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <div>
        <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">Send email</h2>
        <p className="text-sm text-muted-foreground">
          Send a one-off branded message to any address from your HosterDash mailbox.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Compose</CardTitle>
          <CardDescription>The message is wrapped in the HosterDash email template automatically.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="to">Recipient email</Label>
              <Input id="to" type="email" placeholder="customer@example.com" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="subject">Subject</Label>
              <Input id="subject" placeholder="A quick update from HosterDash" value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="message">Message</Label>
              <textarea
                id="message"
                rows={8}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Write your message... Blank lines start a new paragraph."
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            <div className="flex items-center justify-between gap-3">
              {sentTo ? (
                <span className="inline-flex items-center gap-1.5 text-sm text-brand-green">
                  <CheckCircle2 className="h-4 w-4" /> Last sent to {sentTo}
                </span>
              ) : <span />}
              <Button type="submit" disabled={!valid || send.isPending}>
                {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send email
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
